import { useEffect, useId, useRef } from 'react';
import type { AgentEntry } from '../lib/api';
import { prettyAgentTitle } from '../lib/agentName';
import { Prism } from './Prism';

interface SharedProps {
  agents: AgentEntry[];
  activeSlug: string;
  onSelect: (slug: string) => void | Promise<void>;
  onPreview: (slug: string) => void;
  onLogout: () => void | Promise<void>;
  /**
   * Phase 7e: bump the auth revocation epoch, invalidating every cookie
   * issued to this user on every device including this one. Confirms with
   * the user before firing because the action is destructive across
   * devices that aren't visible.
   */
  onSignOutEverywhere: () => void | Promise<void>;
}

export interface AgentSheetProps extends SharedProps {
  open: boolean;
  onClose: () => void;
}

export type AgentRailProps = SharedProps;

/**
 * Phone bottom-sheet per docs/DESIGN-DECISIONS.md §1.3.
 *
 * Anchored to bottom of viewport, takes 75 % of viewport height when open.
 * Handle at top is tinted with the active agent's `--agent-edge` token
 * (locked V1-cloud pattern; brand/BRAND.md "Bottom-sheet handle").
 * Dismiss: tap scrim, tap close icon, or Escape.
 *
 * Phase 4: full 18-agent panel. The deferred banner only renders when the
 * Worker hides agents (e.g. a future phase regression).
 */
export function AgentSheet({ open, onClose, agents, activeSlug, onSelect, onPreview, onLogout, onSignOutEverywhere }: AgentSheetProps) {
  const headingId = useId();
  const triggerRef = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      const t = triggerRef.current as HTMLElement | null;
      if (t && typeof t.focus === 'function') t.focus();
      triggerRef.current = null;
      return;
    }
    triggerRef.current = document.activeElement;
    const first = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const deferredCount = Math.max(0, 18 - Math.max(1, agents.length));

  return (
    <div
      className={
        'simpliloquy-sheet-scrim md:hidden fixed inset-0 z-40 transition-opacity ' +
        (open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')
      }
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="Close agent panel"
        tabIndex={-1}
        className="absolute inset-0 bg-night-900/60 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={
          'simpliloquy-sheet absolute left-0 right-0 bottom-0 bg-night-900 border-t border-night-600 ' +
          'rounded-t-2xl flex flex-col transition-transform ' +
          (open ? 'translate-y-0' : 'translate-y-full')
        }
        style={{
          height: '75dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="flex items-center justify-center pt-2 pb-1">
          <div
            className="h-1 w-9 rounded-full"
            style={{ background: 'var(--agent-edge)' }}
          />
        </div>
        <div className="h-14 flex items-center px-4 border-b border-night-700">
          <h2 id={headingId} className="font-semibold text-sm text-paper-100">Agents</h2>
          <div className="flex-1" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center rounded-md
                       hover:bg-night-800 focus:outline-none focus:bg-night-800"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {agents.map((a) => (
            <AgentRow
              key={a.slug}
              agent={a}
              active={a.slug === activeSlug}
              onSelect={() => {
                void onSelect(a.slug);
                onClose();
              }}
              onPreview={() => {
                onPreview(a.slug);
                onClose();
              }}
              dense={false}
            />
          ))}

          {deferredCount > 0 && (
            <div className="px-4 py-3 border-t border-night-700">
              <div
                className="font-mono text-[10px] uppercase tracking-mono-wide simpliloquy-hint-shimmer"
                style={{ color: 'var(--agent-fg-soft)' }}
              >
                {deferredCount} more agents wire in soon
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-night-700">
          <button
            type="button"
            onClick={() => onLogout()}
            className="w-full h-14 px-4 flex items-center gap-3 hover:bg-night-800
                       text-paper-300 text-sm focus:outline-none focus:bg-night-800"
          >
            <SignOutIcon />
            <span>Sign out</span>
          </button>
          <button
            type="button"
            onClick={() => onSignOutEverywhere()}
            className="w-full h-10 px-4 text-left font-mono text-[10px] uppercase
                       tracking-mono-wide text-paper-500 hover:text-paper-200
                       hover:bg-night-800 focus:outline-none focus:bg-night-800
                       border-t border-night-800"
          >
            sign out everywhere
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tablet+ persistent rail per docs/DESIGN-DECISIONS.md §1.3.
 * 280 px wide, full height. No header, no close affordance.
 * Sign-out at the bottom of the rail.
 */
export function AgentRail({ agents, activeSlug, onSelect, onPreview, onLogout, onSignOutEverywhere }: AgentRailProps) {
  const deferredCount = Math.max(0, 18 - Math.max(1, agents.length));

  return (
    <aside className="hidden md:flex flex-col w-[280px] flex-shrink-0
                      bg-night-900 border-r border-night-700">
      <div className="flex-1 overflow-y-auto">
        {agents.map((a) => (
          <AgentRow
            key={a.slug}
            agent={a}
            active={a.slug === activeSlug}
            onSelect={() => onSelect(a.slug)}
            onPreview={() => onPreview(a.slug)}
            dense
          />
        ))}

        {deferredCount > 0 && (
          <div className="px-4 py-3 border-t border-night-700">
            <div
              className="font-mono text-[10px] uppercase tracking-mono-wide simpliloquy-hint-shimmer"
              style={{ color: 'var(--agent-fg-soft)' }}
            >
              {deferredCount} more agents wire in soon
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-night-700">
        <button
          type="button"
          onClick={() => onLogout()}
          className="w-full h-14 px-4 flex items-center gap-3 hover:bg-night-800
                     text-paper-300 text-sm focus:outline-none focus:bg-night-800"
        >
          <SignOutIcon />
          <span>Sign out</span>
        </button>
        <button
          type="button"
          onClick={() => onSignOutEverywhere()}
          className="w-full h-10 px-4 text-left font-mono text-[10px] uppercase
                     tracking-mono-wide text-paper-500 hover:text-paper-200
                     hover:bg-night-800 focus:outline-none focus:bg-night-800
                     border-t border-night-800"
        >
          sign out everywhere
        </button>
      </div>
    </aside>
  );
}

interface RowProps {
  agent: AgentEntry;
  active: boolean;
  onSelect: () => void;
  onPreview: () => void;
  dense: boolean;
}

function AgentRow({ agent, active, onSelect, onPreview, dense }: RowProps) {
  return (
    <div
      className={
        'w-full text-left flex items-center gap-1 px-4 ' +
        (dense ? 'h-14' : 'h-16') +
        ' relative hover:bg-night-800 transition-colors'
      }
      style={{
        background: active ? 'var(--agent-bg-soft)' : undefined,
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-sm"
          style={{ background: 'var(--agent-accent)' }}
        />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'page' : undefined}
        aria-label={`Spawn ${prettyAgentTitle(agent.slug)}`}
        className="simpliloquy-button-focus flex-1 min-w-0 flex items-center gap-3 h-full -mx-1 px-1 rounded"
      >
        <Prism slug={agent.slug} size={24} active={active} />
        <div className="min-w-0 flex-1 flex flex-col text-left">
          <span className={'font-semibold truncate ' + (dense ? 'text-[14px]' : 'text-[15px]')}>
            {prettyAgentTitle(agent.slug)}
          </span>
          <span
            className="font-mono text-[10px] uppercase tracking-mono-wide truncate"
            style={{ color: 'var(--agent-fg-soft)' }}
          >
            {agent.role}
          </span>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
        aria-label={`Preview ${prettyAgentTitle(agent.slug)}`}
        title="Preview"
        className="simpliloquy-button-focus w-9 h-9 flex items-center justify-center rounded-md
                   text-paper-400 hover:text-paper-200 hover:bg-night-700"
      >
        <InfoIcon />
      </button>
    </div>
  );
}

function InfoIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 16 16" aria-hidden="true">
      <circle cx={8} cy={8} r={6.25} fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx={8} cy={5.2} r={0.85} fill="currentColor" />
      <path
        d="M8 7.5 L8 11.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 3 L13 13 M13 3 L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M9.5 4 L13 4 L13 12 L9.5 12 M10 8 L4 8 M6 5.5 L3.5 8 L6 10.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
